import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fromMinor,
  type LoanInstalment,
  type LoanScheduleImportResult,
  type LoanTrack,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import {
  ConfirmWithReason,
  ErrorState,
  FileField,
  Modal,
  Money,
  Spinner,
  ToneBadge,
} from '@/components/ui';
import { describeError } from '@/lib/errors';
import { formatDate, humanize, rupeesToMinor } from '@/lib/format';
import { LoanFormModal } from './LoanFormModal';
import {
  FREQUENCY_LABEL,
  ITEM_STATUS,
  LENDER_KIND_LABEL,
  LOAN_KIND_LABEL,
  LOAN_STATUS,
  REPAYMENT_MODE_LABEL,
  ratePercent,
} from './loanLabels';

type Dialog = 'edit' | 'cancel' | 'import' | 'charge' | { instalment: LoanInstalment } | null;

/**
 * One loan's full track: its terms, where it stands, every instalment and
 * charge with how far it has got, and what has been done to it.
 */
export function LoanTrackModal({ loanId, onClose }: { loanId: string; onClose(): void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['loan', loanId],
    queryFn: () => api.loans.get(loanId),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['loan', loanId] });
    void queryClient.invalidateQueries({ queryKey: ['loans'] });
  };

  const activate = useMutation({
    mutationFn: () => api.loans.activate(loanId),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => api.loans.cancel(loanId),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  if (isLoading || error || !data) {
    return (
      <Modal title="Loan" onClose={onClose} wide>
        {error ? <ErrorState error={error} /> : <Spinner />}
      </Modal>
    );
  }

  const { loan, instalments, charges, events } = data;
  // Bank loans carry no TDS. A figure already on a row is still shown.
  const showTds = !!loan.tds?.applicable || instalments.some((row) => row.tdsAmount > 0);
  const summary = loan.summary;
  const draft = loan.status === 'DRAFT';
  const active = loan.status === 'ACTIVE';
  const editableRows = active && loan.repaymentMode === 'AUTO_DEBIT' && can('loan:update');

  if (dialog === 'edit') {
    return (
      <LoanFormModal loan={loan} onClose={() => setDialog(null)} onSaved={() => setDialog(null)} />
    );
  }
  if (dialog === 'cancel') {
    return (
      <ConfirmWithReason
        title={`Cancel ${loan.reference}`}
        description="The draft and its schedule are set aside. Nothing has been paid on it."
        actionLabel="Cancel draft"
        requireReason={false}
        pending={cancel.isPending}
        error={cancel.error}
        onClose={() => setDialog(null)}
        onConfirm={() => cancel.mutate()}
      />
    );
  }
  if (dialog === 'import') {
    return <ImportScheduleModal loanId={loanId} onClose={() => setDialog(null)} onDone={refresh} />;
  }
  if (dialog === 'charge') {
    return <AddChargeModal track={data} onClose={() => setDialog(null)} onDone={refresh} />;
  }
  if (dialog) {
    return (
      <EditInstalmentModal
        loanId={loanId}
        instalment={dialog.instalment}
        showTds={showTds}
        onClose={() => setDialog(null)}
        onDone={refresh}
      />
    );
  }

  return (
    <Modal
      title={`${loan.lender.name} · ${loan.reference}`}
      onClose={onClose}
      wide
      footer={
        <>
          {draft && can('loan:delete') ? (
            <button
              type="button"
              className="btn-secondary mr-auto"
              onClick={() => setDialog('cancel')}
            >
              Cancel draft
            </button>
          ) : null}
          {draft && can('loan:update') ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setDialog('import')}>
                Import lender schedule
              </button>
              <button type="button" className="btn-secondary" onClick={() => setDialog('edit')}>
                Edit
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={activate.isPending}
                onClick={() => activate.mutate()}
              >
                {activate.isPending ? 'Activating…' : 'Activate'}
              </button>
            </>
          ) : null}
          {active && can('loan:update') ? (
            <button type="button" className="btn-secondary" onClick={() => setDialog('charge')}>
              Add penal charge
            </button>
          ) : null}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-600">
          <ToneBadge tone={LOAN_STATUS[loan.status]?.tone ?? 'neutral'}>
            {LOAN_STATUS[loan.status]?.label ?? loan.status}
          </ToneBadge>
          <span>
            {LENDER_KIND_LABEL[loan.lender.kind]} · {LOAN_KIND_LABEL[loan.kind]} · A/c{' '}
            {loan.loanAccountNumber}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Disbursed">
            <Money minor={loan.disbursedAmount} />
          </Stat>
          <Stat label="Outstanding">
            <Money minor={summary?.outstanding} />
          </Stat>
          <Stat label="Interest paid">
            <Money minor={summary?.interestPaid} />
          </Stat>
          <Stat label="Next EMI">
            {summary?.nextDue ? (
              <>
                <Money minor={summary.nextDue.amount} />
                <span className="block text-xs text-ink-500">
                  {formatDate(summary.nextDue.dueDate)}
                </span>
              </>
            ) : (
              '—'
            )}
          </Stat>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <Term label="Rate">{ratePercent(loan.rateBasisPoints)} a year</Term>
          <Term label="Repayment">
            {loan.instalmentCount} × {FREQUENCY_LABEL[loan.frequency]?.toLowerCase()}
            {loan.structure === 'BULLET' ? ', bullet' : ''}
          </Term>
          <Term label="EMIs paid by">{REPAYMENT_MODE_LABEL[loan.repaymentMode]}</Term>
          <Term label="Paid">
            {summary?.instalmentsPaid ?? 0} of {summary?.instalmentsTotal ?? instalments.length}
          </Term>
          {loan.moratorium ? (
            <Term label="Moratorium">
              {loan.moratorium.periods} periods,{' '}
              {loan.moratorium.mode === 'CAPITALISE' ? 'interest added' : 'interest only'}
            </Term>
          ) : null}
          {loan.tds?.applicable ? (
            <Term label="TDS on interest">
              {loan.tds.section} · {ratePercent(loan.tds.rateBasisPoints)}
            </Term>
          ) : null}
          {loan.principalLedger ? (
            <Term label="Principal ledger">
              {loan.principalLedger.code} · {loan.principalLedger.name}
            </Term>
          ) : null}
          {loan.interestLedger ? (
            <Term label="Interest ledger">
              {loan.interestLedger.code} · {loan.interestLedger.name}
            </Term>
          ) : null}
          <Term label="Schedule">
            {loan.scheduleSource === 'IMPORTED' ? "Lender's, imported" : 'Worked out by fpc'}
          </Term>
        </dl>

        <section aria-label="Instalments" className="rounded-lg border border-ink-100">
          <h3 className="border-b border-ink-100 bg-ink-50 px-3 py-2 text-sm font-semibold text-ink-800">
            Instalments
          </h3>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-ink-500">
                  <th className="px-3 py-1.5">#</th>
                  <th className="px-3 py-1.5">Due</th>
                  <th className="px-3 py-1.5 text-right">Principal</th>
                  <th className="px-3 py-1.5 text-right">Interest</th>
                  {showTds ? <th className="px-3 py-1.5 text-right">TDS</th> : null}
                  <th className="px-3 py-1.5 text-right">Payable</th>
                  <th className="px-3 py-1.5 text-right">Balance</th>
                  <th className="px-3 py-1.5">Status</th>
                  {editableRows ? <th className="px-3 py-1.5" /> : null}
                </tr>
              </thead>
              <tbody>
                {instalments.map((row) => {
                  const status = ITEM_STATUS[row.displayStatus ?? row.status];
                  return (
                    <tr key={row.id} className="border-t border-ink-100 align-top">
                      <td className="px-3 py-1.5">{row.n}</td>
                      <td className="px-3 py-1.5">{formatDate(row.dueDate)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <Money minor={row.principal} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Money minor={row.interest} />
                      </td>
                      {showTds ? (
                        <td className="px-3 py-1.5 text-right">
                          {row.tdsAmount ? <Money minor={row.tdsAmount} /> : '—'}
                        </td>
                      ) : null}
                      <td className="px-3 py-1.5 text-right font-medium">
                        <Money minor={row.netPayable} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Money minor={row.closing} />
                      </td>
                      <td className="px-3 py-1.5">
                        <ToneBadge tone={status?.tone ?? 'neutral'}>
                          {status?.label ?? humanize(row.status)}
                        </ToneBadge>
                        {row.paidAt ? (
                          <span className="block text-xs text-ink-500">
                            {formatDate(row.paidAt)}
                            {row.bankReference ? ` · ${row.bankReference}` : ''}
                          </span>
                        ) : null}
                        {row.note ? (
                          <span className="block text-xs text-ink-500">{row.note}</span>
                        ) : null}
                      </td>
                      {editableRows ? (
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            className="text-xs font-medium text-brand-700 hover:underline"
                            onClick={() => setDialog({ instalment: row })}
                          >
                            Edit
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {charges.length ? (
          <section aria-label="Charges" className="rounded-lg border border-ink-100">
            <h3 className="border-b border-ink-100 bg-ink-50 px-3 py-2 text-sm font-semibold text-ink-800">
              Charges
            </h3>
            <ul className="divide-y divide-ink-100 text-sm">
              {charges.map((charge) => {
                const status = ITEM_STATUS[charge.displayStatus ?? charge.status];
                return (
                  <li
                    key={charge.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <span>
                      <span className="font-medium">
                        {charge.kind === 'PROCESSING_FEE' ? 'Processing fee' : 'Penal charge'}
                      </span>{' '}
                      · {charge.reason} · due {formatDate(charge.dueDate)}
                    </span>
                    <span className="flex items-center gap-2">
                      <Money minor={charge.amount} />
                      <ToneBadge tone={status?.tone ?? 'neutral'}>
                        {status?.label ?? humanize(charge.status)}
                      </ToneBadge>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {events.length ? (
          <section aria-label="Activity">
            <h3 className="mb-2 text-sm font-semibold text-ink-800">Activity</h3>
            <ol className="space-y-1 text-sm text-ink-600">
              {events.map((event, index) => (
                <li key={index}>
                  <span className="text-ink-500">{formatDate(event.at)}</span> ·{' '}
                  {humanize(event.event.replace(/^loan\./, ''))}
                  {event.by ? ` by ${event.by}` : ''}
                  {event.detail ? ` — ${event.detail}` : ''}
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {activate.error ? (
          <p className="text-sm text-red-700">{describeError(activate.error)}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-100 px-3 py-2">
      <span className="block text-xs text-ink-500">{label}</span>
      <span className="font-semibold text-ink-900">{children}</span>
    </div>
  );
}

function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-500">{label}</dt>
      <dd className="text-ink-800">{children}</dd>
    </div>
  );
}

/** The lender's own schedule, checked first and then used in place of fpc's. */
function ImportScheduleModal({
  loanId,
  onClose,
  onDone,
}: {
  loanId: string;
  onClose(): void;
  onDone(): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<LoanScheduleImportResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.loans.importSchedule(loanId, file!, dryRun),
    onSuccess: (result) => {
      if (result.dryRun) return setChecked(result);
      onDone();
      onClose();
    },
  });
  const ready = checked && !checked.errors.length;

  return (
    <Modal
      title="Import lender schedule"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!file || run.isPending}
            onClick={() => run.mutate(true)}
          >
            Check
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!ready || run.isPending}
            onClick={() => run.mutate(false)}
          >
            Use this schedule
          </button>
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-600">
        One row per instalment with its due date, principal and interest. The principal must add up
        to the amount disbursed.
      </p>
      <FileField
        id="loan-schedule"
        file={file}
        accept=".xlsx,.csv"
        onChange={(next) => {
          setFile(next);
          setChecked(null);
        }}
      />
      {checked ? (
        <div className="mt-3 text-sm">
          <p>
            {checked.rows} instalments · principal <Money minor={checked.totals.principal} /> ·
            interest <Money minor={checked.totals.interest} />
          </p>
          {checked.errors.length ? (
            <ul className="mt-2 list-disc pl-5 text-red-700">
              {checked.errors.slice(0, 10).map((issue, index) => (
                <li key={index}>
                  {issue.row ? `Row ${issue.row}: ` : ''}
                  {issue.message}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-emerald-700">
              Looks right — use it to replace the worked-out schedule.
            </p>
          )}
        </div>
      ) : null}
      {run.error ? <p className="mt-3 text-sm text-red-700">{describeError(run.error)}</p> : null}
    </Modal>
  );
}

/** A late-payment charge, paid like an EMI. */
function AddChargeModal({
  track,
  onClose,
  onDone,
}: {
  track: LoanTrack;
  onClose(): void;
  onDone(): void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [instalmentId, setInstalmentId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api.loans.addCharge(track.loan.id, {
        amount: rupeesToMinor(amount) ?? 0,
        reason,
        ...(instalmentId ? { instalmentId } : {}),
        ...(dueDate ? { dueDate } : {}),
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      title="Add penal charge"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Adding…' : 'Add charge'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label">Amount (₹)</span>
          <input
            className="input"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Due on</span>
          <input
            className="input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">For instalment</span>
          <select
            aria-label="For instalment"
            className="input"
            value={instalmentId}
            onChange={(event) => setInstalmentId(event.target.value)}
          >
            <option value="">Not one in particular</option>
            {track.instalments.map((row) => (
              <option key={row.id} value={row.id}>
                EMI {row.n} · {formatDate(row.dueDate)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">Reason</span>
          <input
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      </div>
      {add.error ? <p className="mt-3 text-sm text-red-700">{describeError(add.error)}</p> : null}
    </Modal>
  );
}

/**
 * An auto-debit payment as the bank actually took it. Once matched to a bank
 * debit its date and reference are the bank's; only the split, TDS and note
 * change, and they must still add up to what was debited.
 */
function EditInstalmentModal({
  loanId,
  instalment,
  showTds,
  onClose,
  onDone,
}: {
  loanId: string;
  instalment: LoanInstalment;
  showTds: boolean;
  onClose(): void;
  onDone(): void;
}) {
  const matched = !!instalment.matched;
  const [form, setForm] = useState({
    dueDate: instalment.dueDate.slice(0, 10),
    principal: String(fromMinor(instalment.principal)),
    interest: String(fromMinor(instalment.interest)),
    tdsAmount: String(fromMinor(instalment.tdsAmount)),
    bankReference: instalment.bankReference ?? '',
    note: instalment.note ?? '',
    reason: '',
  });
  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));
  const net =
    (rupeesToMinor(form.principal) ?? 0) +
    (rupeesToMinor(form.interest) ?? 0) -
    (rupeesToMinor(form.tdsAmount) ?? 0);

  const save = useMutation({
    mutationFn: () =>
      api.loans.editInstalment(loanId, instalment.id, {
        ...(matched ? {} : { dueDate: form.dueDate, bankReference: form.bankReference }),
        principal: rupeesToMinor(form.principal) ?? 0,
        interest: rupeesToMinor(form.interest) ?? 0,
        tdsAmount: rupeesToMinor(form.tdsAmount) ?? 0,
        note: form.note,
        reason: form.reason,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      title={`Edit EMI ${instalment.n}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending || form.reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {matched ? (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Matched to a bank debit of <Money minor={instalment.netPayable} />: its date and reference
          are the bank&apos;s. Principal + interest{showTds ? ' − TDS' : ''} must still come to
          that.
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label">Debit date</span>
          <input
            className="input"
            type="date"
            disabled={matched}
            value={form.dueDate}
            onChange={(event) => set({ dueDate: event.target.value })}
          />
        </label>
        <label className="block">
          <span className="label">Bank reference</span>
          <input
            className="input"
            disabled={matched}
            value={form.bankReference}
            onChange={(event) => set({ bankReference: event.target.value })}
          />
        </label>
        <label className="block">
          <span className="label">Principal (₹)</span>
          <input
            className="input"
            value={form.principal}
            onChange={(event) => set({ principal: event.target.value })}
          />
        </label>
        <label className="block">
          <span className="label">Interest (₹)</span>
          <input
            className="input"
            value={form.interest}
            onChange={(event) => set({ interest: event.target.value })}
          />
        </label>
        {showTds ? (
          <label className="block">
            <span className="label">TDS (₹)</span>
            <input
              className="input"
              value={form.tdsAmount}
              onChange={(event) => set({ tdsAmount: event.target.value })}
            />
          </label>
        ) : null}
        <div>
          <span className="label">Payable</span>
          <p
            className={`py-2 font-semibold ${matched && net !== instalment.netPayable ? 'text-red-700' : 'text-ink-900'}`}
          >
            <Money minor={net} />
          </p>
        </div>
        <label className="block sm:col-span-2">
          <span className="label">Note</span>
          <input
            className="input"
            value={form.note}
            onChange={(event) => set({ note: event.target.value })}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="label">
            Reason for the change<span className="text-red-600"> *</span>
          </span>
          <input
            className="input"
            value={form.reason}
            onChange={(event) => set({ reason: event.target.value })}
          />
        </label>
      </div>
      {save.error ? <p className="mt-3 text-sm text-red-700">{describeError(save.error)}</p> : null}
    </Modal>
  );
}
